import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  getGbp, runGbpCrawl, updateGbpFrequency,
  type GbpData, type GbpReview, type GbpFullFreq, type GbpNewFreq,
} from "../lib/authClient";
import { siteKey } from "../lib/siteData";

// GBP Review Manager — the connected Google Business Profile's reviews over time (colored by
// rating), a reviews list, the dual crawl schedule, and a record of reviews Google has removed.
// The profile is chosen in ⚙ Settings → Saved Websites (DataForSEO search); this tab
// displays/tracks it. DB is the single source of truth (no localStorage).

interface Props { siteUrl?: string; siteBrand?: string; onOpenSettings?: () => void; refreshToken?: number }

const ratingColor = (r: number) => (r <= 2 ? "#dc2626" : r === 3 ? "#ea580c" : "#16a34a");
const FULL_FREQS: { v: GbpFullFreq; label: string }[] = [
  { v: "weekly", label: "Weekly" }, { v: "biweekly", label: "Every 2 weeks" }, { v: "monthly", label: "Monthly" },
];
const NEW_FREQS: { v: GbpNewFreq; label: string }[] = [
  { v: "daily", label: "Daily" }, { v: "every_3_days", label: "Every 3 days" }, { v: "weekly", label: "Weekly" }, { v: "biweekly", label: "Every 2 weeks" },
];
const WINDOWS: { label: string; days: number }[] = [
  { label: "Last 30 days", days: 30 }, { label: "Last 60 days", days: 60 }, { label: "Last 90 days", days: 90 },
  { label: "6 months", days: 182 }, { label: "12 months", days: 365 }, { label: "All time", days: 0 },
];

function fmtDate(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? (iso || "").slice(0, 10) : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function stars(r: number): string { return "★★★★★".slice(0, Math.max(0, Math.min(5, r))) + "☆☆☆☆☆".slice(0, 5 - Math.max(0, Math.min(5, r))); }

// ── Reviews-over-time chart: cumulative-count line, one dot per review colored by rating.
// Supports a date-window filter (from the parent) and drag-to-zoom into a date range. ──
interface HoverPt { leftPct: number; topPct: number; author: string; count: number; date: string; text: string; color: string; rating: number }
// Fixed chart geometry (viewBox units) — hoisted so the wheel handler can use it too.
const W = 1000, CH = 240, padL = 34, padR = 14, padT = 14, padB = 24;
const plotW = W - padL - padR, plotH = CH - padT - padB;

function ReviewsChart({ reviews, windowDays, zoom, onZoom }: {
  reviews: GbpReview[]; windowDays: number; zoom: { start: number; end: number } | null; onZoom: (z: { start: number; end: number } | null) => void;
}) {
  const [hover, setHover] = useState<HoverPt | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  // Live copies of the current domain / onZoom so the (stable) wheel listener always reads fresh values.
  const domRef = useRef({ domStart: 0, domEnd: 0, dataStart: 0, now: 0, windowDays: 0 });
  const onZoomRef = useRef(onZoom);
  onZoomRef.current = onZoom;

  // Global cumulative count over ALL dated reviews (so the running total stays true even when a
  // window/zoom hides earlier points).
  const isDark = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";
  const allPts = useMemo(() => {
    const dated = reviews.map(r => ({ t: Date.parse(r.reviewDate), r })).filter(p => !isNaN(p.t)).sort((a, b) => a.t - b.t);
    return dated.map((p, i) => ({ t: p.t, count: i + 1, rating: p.r.rating, author: p.r.author, text: p.r.text, iso: p.r.reviewDate }));
  }, [reviews]);

  // Scroll-wheel zoom (centered on the cursor). Attached as a non-passive native listener via a
  // ref callback so preventDefault works (React's onWheel is passive). Zooming out past the full
  // window clears the zoom (back to the dropdown's range).
  const onWheel = useCallback((e: WheelEvent) => {
    const svg = svgRef.current; if (!svg) return;
    e.preventDefault();
    const s = domRef.current;
    const span = s.domEnd - s.domStart; if (span <= 0) return;
    const rect = svg.getBoundingClientRect(); if (!rect.width) return;
    const px = Math.max(padL, Math.min(W - padR, ((e.clientX - rect.left) / rect.width) * W));
    const tCursor = s.domStart + ((px - padL) / plotW) * span;
    const fullStart = s.windowDays > 0 ? s.now - s.windowDays * 86400000 : s.dataStart;
    const fullSpan = Math.max(1, s.now - fullStart);
    const MIN_SPAN = 2 * 86400000; // 2 days
    let newSpan = span * (e.deltaY < 0 ? 0.8 : 1.25);
    if (newSpan >= fullSpan) { onZoomRef.current(null); return; } // zoomed all the way out → reset
    if (newSpan < MIN_SPAN) newSpan = MIN_SPAN;
    const frac = (tCursor - s.domStart) / span;
    let newStart = tCursor - frac * newSpan;
    let newEnd = newStart + newSpan;
    if (newStart < fullStart) { newStart = fullStart; newEnd = fullStart + newSpan; }
    if (newEnd > s.now) { newEnd = s.now; newStart = s.now - newSpan; if (newStart < fullStart) newStart = fullStart; }
    onZoomRef.current({ start: newStart, end: newEnd });
  }, []);
  const setSvg = useCallback((node: SVGSVGElement | null) => {
    if (svgRef.current) svgRef.current.removeEventListener("wheel", onWheel);
    svgRef.current = node;
    if (node) node.addEventListener("wheel", onWheel, { passive: false });
  }, [onWheel]);

  if (!allPts.length) return <div style={{ fontSize: 12, color: (isDark ? "#8b98ad" : "#94a3b8") }}>No dated reviews to chart yet.</div>;

  const now = Date.now();
  const dataStart = allPts[0].t;
  let domStart: number, domEnd: number;
  if (zoom) { domStart = zoom.start; domEnd = zoom.end; }
  else if (windowDays > 0) { domStart = now - windowDays * 86400000; domEnd = now; }
  else { domStart = dataStart; domEnd = now; }
  if (domEnd <= domStart) domEnd = domStart + 86400000;
  domRef.current = { domStart, domEnd, dataStart, now, windowDays };

  const inView = allPts.filter(p => p.t >= domStart && p.t <= domEnd);
  const maxC = allPts[allPts.length - 1].count;
  const x = (t: number) => padL + ((t - domStart) / (domEnd - domStart)) * plotW;
  const y = (c: number) => padT + plotH - (c / maxC) * plotH;
  const yTicks = [0, Math.round(maxC / 2), maxC];
  const xTicks = Array.from({ length: 5 }, (_, i) => domStart + (i / 4) * (domEnd - domStart));
  const fmtTick = (t: number) => new Date(t).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  const above = (hover?.topPct ?? 100) > 30;
  const hx = !hover ? "-50%" : hover.leftPct <= 16 ? "0%" : hover.leftPct >= 84 ? "-100%" : "-50%";

  return (
    <div>
      <div style={{ position: "relative" }}>
        <svg
          ref={setSvg} viewBox={`0 0 ${W} ${CH}`} width="100%" style={{ display: "block", maxWidth: "100%" }}
          role="img" aria-label="Reviews over time"
          onMouseLeave={() => setHover(null)}
        >
          {yTicks.map(v => (
            <g key={v}>
              <line x1={padL} y1={y(v)} x2={W - padR} y2={y(v)} stroke={isDark ? "#2a3345" : "#eef2f6"} strokeWidth={1} />
              <text x={padL - 5} y={y(v) + 3} textAnchor="end" fontSize={6.4} fill={isDark ? "#8b98ad" : "#94a3b8"} fontFamily="'Inter','Mulish',sans-serif">{v}</text>
            </g>
          ))}
          {xTicks.map((t, i) => (
            <text key={i} x={x(t)} y={CH - 7} textAnchor={i === 0 ? "start" : i === 4 ? "end" : "middle"} fontSize={6.4} fill={isDark ? "#8b98ad" : "#94a3b8"} fontFamily="'Inter','Mulish',sans-serif">{fmtTick(t)}</text>
          ))}
          {inView.length > 1 && <polyline fill="none" stroke={isDark ? "#3a4658" : "#cbd5e1"} strokeWidth={1.4} vectorEffect="non-scaling-stroke" points={inView.map(p => `${x(p.t)},${y(p.count)}`).join(" ")} />}
          {inView.map((p, i) => (
            <g key={i}>
              <circle cx={x(p.t)} cy={y(p.count)} r={3} fill={ratingColor(p.rating)} />
              <circle cx={x(p.t)} cy={y(p.count)} r={9} fill="transparent" style={{ cursor: "pointer" }}
                onMouseEnter={() => setHover({ leftPct: (x(p.t) / W) * 100, topPct: (y(p.count) / CH) * 100, author: p.author, count: p.count, date: fmtDate(p.iso), text: p.text, color: ratingColor(p.rating), rating: p.rating })}
                onMouseLeave={() => setHover(null)}
              />
            </g>
          ))}
        </svg>
        {hover && (
          <div style={{
            position: "absolute", left: `${hover.leftPct}%`, top: `${hover.topPct}%`,
            transform: `translate(${hx}, ${above ? "calc(-100% - 9px)" : "9px"})`,
            background: (isDark ? "#16223a" : "#fff"), color: (isDark ? "#d0d9e6" : "#334155"), border: `1px solid ${isDark ? "#2f3a4d" : "#e2e8f0"}`, borderRadius: 6,
            boxShadow: "0 4px 14px rgba(15,23,42,.14)", padding: "8px 11px", fontSize: 11, lineHeight: 1.45,
            fontFamily: "'Inter','Mulish',sans-serif", width: 300, pointerEvents: "none", zIndex: 5, whiteSpace: "normal",
          }}>
            <div><span style={{ color: hover.color, fontWeight: 700 }}>{hover.author || "Anonymous"}</span><span style={{ color: (isDark ? "#93a4c0" : "#64748b") }}> · review #{hover.count}</span></div>
            <div style={{ fontSize: 13, letterSpacing: 1, marginTop: 2 }}>
              {[0, 1, 2, 3, 4].map(i => <span key={i} style={{ color: i < hover.rating ? "#fbbc04" : (isDark ? "#333e4f" : "#d1d5db") }}>★</span>)}
            </div>
            <div style={{ color: (isDark ? "#93a4c0" : "#64748b"), fontSize: 10.5, marginTop: 1 }}>{hover.date}</div>
            {hover.text && <div style={{ color: (isDark ? "#e6edf6" : "#0f172a"), marginTop: 4 }}>{hover.text}</div>}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 14, marginTop: 6, fontSize: 10.5, color: (isDark ? "#aeb9cc" : "#475569") }}>
        {[["≤ 2★", "#dc2626"], ["3★", "#ea580c"], ["4–5★", "#16a34a"]].map(([l, c]) => (
          <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 9, height: 9, borderRadius: "50%", background: c, display: "inline-block" }} />{l}
          </span>
        ))}
        <span style={{ marginLeft: "auto", color: (isDark ? "#8b98ad" : "#94a3b8") }}>Scroll over the chart to zoom.</span>
      </div>
    </div>
  );
}


export function GbpReviewManager({ siteUrl, siteBrand, onOpenSettings, refreshToken }: Props) {
  const isDark = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";
  const SECTION: React.CSSProperties = { background: (isDark ? "#16223a" : "#fff"), border: `1px solid ${isDark ? "#2f3a4d" : "#e2e8f0"}`, borderRadius: 12, padding: "18px 20px", marginBottom: 18 };
  const H: React.CSSProperties = { fontSize: 15, fontWeight: 500, color: (isDark ? "#ffffff" : "#f97316"), marginBottom: 12, fontFamily: "'Oswald','Barlow Condensed','Inter',sans-serif", textTransform: "uppercase", letterSpacing: .7 };
  const selStyle: React.CSSProperties = { padding: "7px 10px", border: `1px solid ${isDark ? "#3a4658" : "#cbd5e1"}`, borderRadius: 6, fontSize: 12.5, fontFamily: "'Inter','Mulish',sans-serif", color: (isDark ? "#e6edf6" : "#0f172a"), background: (isDark ? "#16223a" : "#fff") };
  const btnStyle: React.CSSProperties = { padding: "7px 14px", border: "none", borderRadius: 0, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" };
  const key = siteUrl ? siteKey(siteUrl) : "";
  const [data, setData] = useState<GbpData>({ profile: null, reviews: [], removed: [] });
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState<"full" | "new" | null>(null);
  const [status, setStatus] = useState<{ tone: "info" | "ok" | "err"; text: string } | null>(null);
  const [filter, setFilter] = useState<"all" | "needs">("all");
  const [chartWindow, setChartWindow] = useState<number>(0); // days; 0 = all time
  const [chartZoom, setChartZoom] = useState<{ start: number; end: number } | null>(null);

  const load = useCallback(async () => {
    if (!key) return;
    setLoading(true);
    try {
      const d = await getGbp(key);
      setData(d); setLoaded(true);
    } catch (e) { setStatus({ tone: "err", text: e instanceof Error ? e.message : "Could not load reviews." }); }
    finally { setLoading(false); }
  }, [key]);

  // Bumped on site change AND unmount so any in-flight background-crawl poll loop stops itself.
  const pollGen = useRef(0);
  useEffect(() => { pollGen.current++; setData({ profile: null, reviews: [], removed: [] }); setLoaded(false); setStatus(null); setChartZoom(null); setRunning(null); }, [key]);
  useEffect(() => () => { pollGen.current++; }, []);
  useEffect(() => { void load(); }, [load]);
  // Reload when a site-level change is signaled (e.g. connecting this site's GBP in Settings), so the
  // tab populates the newly-connected profile instead of staying on "No GBP connected". Skip the
  // initial mount — the [load] effect above already handles the first fetch.
  const didInitRefresh = useRef(false);
  useEffect(() => {
    if (!didInitRefresh.current) { didInitRefresh.current = true; return; }
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const profile = data.profile;

  // The crawl runs in the BACKGROUND (large profiles take minutes — longer than the request holds),
  // so we start it, then poll /api/gbp until the profile's last-run timestamp advances (crawl done)
  // or a ~7-minute window elapses. pollGen cancels the loop on a new crawl, site change, or unmount.
  async function crawl(mode: "full" | "new") {
    if (!key || running) return;
    const myGen = ++pollGen.current;
    const lastRunOf = (p: typeof data.profile) => (mode === "full" ? p?.fullLastRunAt : p?.newLastRunAt) ?? "";
    setRunning(mode);
    setStatus({ tone: "info", text: mode === "full"
      ? "Crawling all reviews in the background — a large profile can take a few minutes. This updates automatically when it finishes…"
      : "Checking for new reviews in the background…" });
    try {
      const r = await runGbpCrawl(key, mode);
      if (myGen !== pollGen.current) return;
      setData({ profile: r.profile, reviews: r.reviews, removed: r.removed });
      if (r.alreadyRunning) setStatus({ tone: "info", text: "A crawl is already running for this profile — it'll update here when it finishes…" });
      const baselineRun = lastRunOf(r.profile);
      const startedAt = Date.now();
      const WINDOW_MS = 7 * 60 * 1000;
      const poll = async () => {
        if (myGen !== pollGen.current) return; // superseded by a new crawl / site change / unmount
        let done = false;
        try {
          const d = await getGbp(key);
          if (myGen !== pollGen.current) return;
          setData(d);
          if (lastRunOf(d.profile) !== baselineRun) {
            done = true;
            setStatus({ tone: "ok", text: `✓ Crawl complete — ${d.reviews.length} review${d.reviews.length === 1 ? "" : "s"} on record${mode === "full" && d.removed.length ? ` · ${d.removed.length} removed on record` : ""}.` });
          }
        } catch { /* transient — keep polling */ }
        if (myGen !== pollGen.current) return;
        if (done) { setRunning(null); return; }
        if (Date.now() - startedAt < WINDOW_MS) window.setTimeout(poll, 15000);
        else { setStatus({ tone: "info", text: "Still processing — a large crawl can take several minutes. Leave this tab open or refresh shortly to see the reviews." }); setRunning(null); }
      };
      window.setTimeout(poll, 15000);
    } catch (e) {
      if (myGen === pollGen.current) { setStatus({ tone: "err", text: e instanceof Error ? e.message : "Crawl failed." }); setRunning(null); }
    }
  }

  async function changeFreq(patch: { fullFrequency?: GbpFullFreq; newFrequency?: GbpNewFreq; enabled?: boolean }) {
    if (!key || !profile) return;
    setData(d => ({ ...d, profile: d.profile ? { ...d.profile, ...patch } : d.profile }));
    try { await updateGbpFrequency(key, patch); }
    catch (e) { setStatus({ tone: "err", text: e instanceof Error ? e.message : "Could not update schedule." }); void load(); }
  }

  const mapsUrl = profile?.cid ? `https://maps.google.com/?cid=${profile.cid}` : (profile?.profileUrl || "https://business.google.com/reviews");

  if (!key) {
    return <div style={{ padding: "40px 16px", textAlign: "center", color: (isDark ? "#93a4c0" : "#64748b"), fontFamily: "'Inter','Mulish',sans-serif" }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: (isDark ? "#e6edf6" : "#0f172a"), marginBottom: 6 }}>Select a website first</div>
      <div style={{ fontSize: 13 }}>Choose or connect a website, then connect its Google Business Profile in Settings.</div>
    </div>;
  }

  if (loaded && !profile) {
    return <div style={{ padding: "48px 16px", textAlign: "center", color: (isDark ? "#93a4c0" : "#64748b"), fontFamily: "'Inter','Mulish',sans-serif" }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: (isDark ? "#e6edf6" : "#0f172a"), marginBottom: 8 }}>No Google Business Profile connected</div>
      <div style={{ fontSize: 13, maxWidth: 460, margin: "0 auto 16px", lineHeight: 1.6 }}>
        Connect this website&rsquo;s Google Business Profile in <strong>Settings → Saved Websites</strong> (the “GBP Profile” link) to start tracking its reviews here.
      </div>
      {onOpenSettings && <button style={{ ...btnStyle, background: "#2563eb", color: (isDark ? "#16223a" : "#fff"), padding: "9px 18px" }} onClick={onOpenSettings}>Open Settings → Saved Websites</button>}
    </div>;
  }

  const needsReply = data.reviews.filter(r => !r.ownerReply);
  const shown = filter === "needs" ? needsReply : data.reviews;

  const removedByMonth = new Map<string, GbpReview[]>();
  for (const r of data.removed) {
    const m = r.removedAt ? new Date(r.removedAt).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "Unknown";
    (removedByMonth.get(m) ?? removedByMonth.set(m, []).get(m)!).push(r);
  }

  return (
    <div style={{ fontFamily: "'Inter','Mulish',sans-serif", color: (isDark ? "#d0d9e6" : "#334155"), marginTop: 52 }}>
      {status && <div style={{ fontSize: 12.5, marginBottom: 12, color: status.tone === "ok" ? "#16a34a" : status.tone === "err" ? "#dc2626" : "#2563eb" }}>{status.text}</div>}

      {/* Business info header */}
      {profile && (
        <div style={{ ...SECTION, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: "1 1 320px" }}>
            <div style={{ fontFamily: "'Oswald', 'Barlow Condensed', 'Inter', sans-serif", fontSize: 24, fontWeight: 500, color: (isDark ? "#ffffff" : "#f97316"), textTransform: "uppercase", letterSpacing: "0.7px" }}>{profile.businessName || siteBrand || "Business Profile"}</div>
            {profile.address && <div style={{ fontSize: 12.5, color: (isDark ? "#93a4c0" : "#64748b"), marginTop: 2 }}>{profile.address}</div>}
            {(profile.category || profile.additionalCategories.length > 0) && (
              <div style={{ fontSize: 12.5, color: (isDark ? "#d0d9e6" : "#334155"), marginTop: 6 }}>
                {profile.category && <span style={{ fontWeight: 700 }}>{profile.category}</span>}
                {profile.additionalCategories.length > 0 && <span style={{ color: (isDark ? "#93a4c0" : "#64748b") }}>{profile.category ? " · " : ""}{profile.additionalCategories.join(", ")}</span>}
              </div>
            )}
            {profile.phone && <div style={{ fontSize: 12.5, color: (isDark ? "#d0d9e6" : "#334155"), marginTop: 4 }}>📞 {profile.phone}</div>}
            <div style={{ fontSize: 13, color: (isDark ? "#d0d9e6" : "#334155"), marginTop: 8, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
              <span><strong style={{ color: "#ea580c" }}>{profile.rating != null ? profile.rating.toFixed(1) : "—"}</strong> avg rating</span>
              <span><strong>{profile.reviewCount != null ? profile.reviewCount.toLocaleString() : data.reviews.length}</strong> reviews</span>
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}>View on Google ↗</a>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
            {profile.hours.length > 0 && (
              <div style={{ fontSize: 11, color: (isDark ? "#aeb9cc" : "#475569"), lineHeight: 1.55, minWidth: 190 }}>
                <div style={{ fontWeight: 700, color: (isDark ? "#93a4c0" : "#64748b"), textTransform: "uppercase", letterSpacing: .4, marginBottom: 3 }}>Hours</div>
                {profile.hours.map(h => (
                  <div key={h.day} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <span style={{ color: (isDark ? "#93a4c0" : "#64748b") }}>{h.day}</span><span>{h.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reviews over time */}
      <div style={SECTION}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
          <div style={{ ...H, marginBottom: 0 }}>Reviews Over Time</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {chartZoom && <button style={{ ...btnStyle, background: (isDark ? "#17202f" : "#f1f5f9"), color: (isDark ? "#aeb9cc" : "#475569"), borderRadius: 6, padding: "6px 12px" }} onClick={() => setChartZoom(null)}>Reset zoom</button>}
            <select value={chartWindow} onChange={e => { setChartWindow(Number(e.target.value)); setChartZoom(null); }} style={selStyle}>
              {WINDOWS.map(w => <option key={w.days} value={w.days}>{w.label}</option>)}
            </select>
          </div>
        </div>
        {loading && !data.reviews.length ? <div style={{ fontSize: 12, color: (isDark ? "#8b98ad" : "#94a3b8") }}>Loading…</div>
          : data.reviews.length ? <ReviewsChart reviews={data.reviews} windowDays={chartWindow} zoom={chartZoom} onZoom={setChartZoom} />
          : <div style={{ fontSize: 12.5, color: (isDark ? "#8b98ad" : "#94a3b8") }}>No reviews stored yet. Use <strong>Crawl All Reviews</strong> below to pull them.</div>}
      </div>

      {/* Scrape schedule */}
      <div style={SECTION}>
        <div style={H}>Automatic Review Checks</div>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <label style={{ display: "block", fontSize: 10.5, fontWeight: 700, color: (isDark ? "#93a4c0" : "#64748b"), marginBottom: 4, textTransform: "uppercase", letterSpacing: .4 }}>Full crawl (finds removed)</label>
            <select value={profile?.fullFrequency ?? "monthly"} onChange={e => void changeFreq({ fullFrequency: e.target.value as GbpFullFreq })} style={selStyle}>
              {FULL_FREQS.map(f => <option key={f.v} value={f.v}>{f.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 10.5, fontWeight: 700, color: (isDark ? "#93a4c0" : "#64748b"), marginBottom: 4, textTransform: "uppercase", letterSpacing: .4 }}>New-reviews check</label>
            <select value={profile?.newFrequency ?? "weekly"} onChange={e => void changeFreq({ newFrequency: e.target.value as GbpNewFreq })} style={selStyle}>
              {NEW_FREQS.map(f => <option key={f.v} value={f.v}>{f.label}</option>)}
            </select>
          </div>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 600, color: (isDark ? "#d0d9e6" : "#334155"), cursor: "pointer" }}>
            <input type="checkbox" checked={profile?.enabled ?? true} onChange={e => void changeFreq({ enabled: e.target.checked })} /> Enabled
          </label>
          <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
            <button style={{ ...btnStyle, background: running ? "#93c5a3" : "#16a34a", color: (isDark ? "#16223a" : "#fff"), cursor: running ? "not-allowed" : "pointer" }} disabled={!!running} onClick={() => void crawl("new")}>{running === "new" ? "Checking…" : "Check New Reviews"}</button>
            <button style={{ ...btnStyle, background: running ? "#93c5a3" : (isDark ? "#5eead4" : "#0f766e"), color: (isDark ? "#16223a" : "#fff"), cursor: running ? "not-allowed" : "pointer" }} disabled={!!running} onClick={() => void crawl("full")}>{running === "full" ? "Crawling…" : "Crawl All Reviews"}</button>
          </div>
        </div>
        <div style={{ fontSize: 11, color: (isDark ? "#8b98ad" : "#94a3b8"), marginTop: 10 }}>
          {data.reviews.length} stored review{data.reviews.length === 1 ? "" : "s"} · Last All Reviews Check: {fmtDate(profile?.fullLastRunAt)} · Last New Reviews Check: {fmtDate(profile?.newLastRunAt)}. A full crawl re-pulls every live review to detect removals.
        </div>
      </div>

      {/* Reviews list */}
      <div style={SECTION}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ ...H, marginBottom: 0 }}>Reviews {needsReply.length > 0 && <span style={{ color: "#dc2626", fontSize: 11 }}>· {needsReply.length} need a reply</span>}</div>
          <div style={{ display: "flex", border: `1px solid ${isDark ? "#3a4658" : "#cbd5e1"}`, borderRadius: 0, overflow: "hidden" }}>
            {(["all", "needs"] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{ padding: "6px 12px", border: "none", borderRadius: 0, background: filter === f ? "#2563eb" : (isDark ? "#16223a" : "#fff"), color: filter === f ? (isDark ? "#16223a" : "#fff") : (isDark ? "#aeb9cc" : "#475569"), fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
                {f === "all" ? "All" : "Needs reply"}
              </button>
            ))}
          </div>
        </div>
        {shown.length === 0 ? <div style={{ fontSize: 12.5, color: (isDark ? "#8b98ad" : "#94a3b8") }}>{data.reviews.length ? "No reviews match this filter." : "No reviews yet."}</div> : (
          <div style={{ maxHeight: 560, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12, paddingRight: 4 }}>
            {shown.map(r => (
              <div key={r.reviewId} style={{ border: `1px solid ${isDark ? "#2f3a4d" : "#e2e8f0"}`, borderRadius: 10, padding: "12px 14px", background: (isDark ? "#131b2b" : "#f8fafc") }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: (isDark ? "#e6edf6" : "#0f172a") }}>{r.author || "Anonymous"}</div>
                  <div style={{ fontSize: 11, color: (isDark ? "#8b98ad" : "#94a3b8"), whiteSpace: "nowrap" }}>{fmtDate(r.reviewDate)}</div>
                </div>
                <div style={{ fontSize: 13, color: ratingColor(r.rating), letterSpacing: 1, margin: "2px 0 6px" }}>{stars(r.rating)}</div>
                {r.text && <div style={{ fontSize: 12.5, color: (isDark ? "#d0d9e6" : "#334155"), lineHeight: 1.5, marginBottom: 8 }}>{r.text}</div>}
                {r.ownerReply ? (
                  <div style={{ fontSize: 12, color: (isDark ? "#86efac" : "#166534"), background: (isDark ? "#12241a" : "#f0fdf4"), border: `1px solid ${isDark ? "#2b5c3f" : "#bbf7d0"}`, borderRadius: 8, padding: "7px 10px" }}>
                    <strong>Owner reply:</strong> {r.ownerReply}
                  </div>
                ) : (
                  <div style={{ display: "inline-block", fontSize: 10.5, fontWeight: 700, color: (isDark ? "#fcd34d" : "#b45309"), background: (isDark ? "#241f10" : "#fffbeb"), border: `1px solid ${isDark ? "#5c4f2b" : "#fde68a"}`, borderRadius: 20, padding: "2px 9px" }}>Needs reply</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Removed reviews */}
      <div style={SECTION}>
        <div style={H}>Removed Reviews {data.removed.length > 0 && <span style={{ color: (isDark ? "#8b98ad" : "#94a3b8"), fontSize: 11 }}>· {data.removed.length} total</span>}</div>
        <div style={{ fontSize: 11.5, color: (isDark ? "#8b98ad" : "#94a3b8"), marginBottom: 12 }}>Reviews that were live before but disappeared on a later full crawl — a dated record you can submit to Google as proof.</div>
        {data.removed.length === 0 ? <div style={{ fontSize: 12.5, color: (isDark ? "#8b98ad" : "#94a3b8") }}>None detected yet.</div> : (
          [...removedByMonth.entries()].map(([month, list]) => (
            <div key={month} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: (isDark ? "#aeb9cc" : "#475569"), textTransform: "uppercase", letterSpacing: .4, marginBottom: 6 }}>{month} · {list.length}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {list.map(r => (
                  <div key={r.reviewId} style={{ border: `1px solid ${isDark ? "#5c2b2b" : "#fecaca"}`, borderRadius: 10, padding: "10px 12px", background: (isDark ? "#241214" : "#fef2f2") }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: (isDark ? "#e6edf6" : "#0f172a") }}>{r.author || "Anonymous"} <span style={{ color: ratingColor(r.rating) }}>{stars(r.rating)}</span></div>
                      <div style={{ fontSize: 11, color: (isDark ? "#8b98ad" : "#94a3b8"), whiteSpace: "nowrap" }}>Removed {fmtDate(r.removedAt)}</div>
                    </div>
                    {r.text && <div style={{ fontSize: 12, color: (isDark ? "#d0d9e6" : "#334155"), lineHeight: 1.5, marginTop: 4 }}>{r.text}</div>}
                    <div style={{ fontSize: 10.5, color: (isDark ? "#8b98ad" : "#94a3b8"), marginTop: 4 }}>Originally posted {fmtDate(r.reviewDate)}</div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
